import React from 'react'
import { ForwardRefExoticComponent, SVGProps } from 'react'

import { FormsElementInput, FormsElementTypeEnum } from 'generated/resources/datatable.graphql'

import { Answer, ElementOption, booleanParam, elementParam, numberParam } from './types'

const AllowedParamTypes = new Set(['string', 'number', 'boolean'])

function isValidParam<T>(value: unknown, defaultValue?: T): value is T {
  //if we have a default value, param is valid if the same type
  if (defaultValue) {
    return typeof value === typeof defaultValue
  }

  //we don't have default value, param is valid if is string, number or boolean
  return AllowedParamTypes.has(typeof value)
}

/**
 * Base element class that all element types will extend.
 * Contains common attributes and methods for all element types.
 */
export abstract class Element {
  /**
   * Unique identifier for the element
   */
  id: string

  /**
   * The label of the element
   */
  label: string

  /**
   * The description of the element
   */
  description: string

  /**
   * The position of the element in the form or in the section
   */
  position: number

  /**
   * The section of the element
   */
  sectionId: string | null

  /**
   * Whether the element is mandatory to answer
   */
  mandatory: boolean

  /**
   * Custom parameters specific to the element
   */
  params: Record<string, unknown>

  options: ElementOption[]

  /**
   * Constructor for the Element class
   *
   * @param id - Unique identifier for the element
   * @param label - The label of the element
   * @param description - The description of the element
   * @param position - The position of the element in the form or in the section
   * @param mandatory - Whether the element is mandatory to answer
   * @param params - Custom parameters specific to the element
   * @param options - Options for this element
   * @param sectionId - The section of the element
   */
  constructor(
    id: string,
    label: string,
    description: string,
    position: number,
    mandatory = false,
    params: Record<string, unknown> = {},
    options: ElementOption[] = [],
    sectionId: string | null = null
  ) {
    this.id = id
    this.label = label
    this.description = description
    this.position = position
    this.mandatory = mandatory
    this.params = params
    this.options = options
    this.sectionId = sectionId

    this.configureParams()
  }

  /**
   * Get the type of the element
   */
  abstract getType(): FormsElementTypeEnum

  /**
   * Get the icon of the element
   */
  abstract getIcon(): ForwardRefExoticComponent<SVGProps<SVGSVGElement>>

  /**
   * Validate the element's configuration
   */
  abstract validate(): boolean

  /**
   * Get a React component for editing this element
   *
   * @param _onChange - Callback function to handle changes to the element
   * @returns A React component for editing this element
   */

  getEditorComponent(_onChange: (element: Element) => void): React.ReactNode {
    return null
  }

  getPreviewComponent(): React.ReactNode {
    return null
  }

  getQuestionComponent(_onChange: (answer: Answer) => void): React.ReactNode {
    return null
  }

  /**
   * Get a custom parameter value
   *
   * @param key - The key of the custom parameter
   * @param defaultValue - The default value to return if the parameter doesn't exist
   */
  getParam<T>(key: string, defaultValue?: T): T | undefined {
    const value = this.params[key]

    if (key in this.params && isValidParam<T>(value, defaultValue)) {
      return value
    }

    return defaultValue
  }

  /**
   * Set a custom parameter value
   *
   * @param key - The key of the custom parameter
   * @param value - The value to set
   */
  setParam<T>(key: string, value: T): void {
    this.params[key] = value
  }

  /**
   * Deletes a custom parameter value
   *
   * @param key - The key of the custom parameter
   */
  deleteParam(key: string): void {
    delete this.params[key]
  }

  getDefaultParams(): Record<string, unknown> {
    return {}
  }

  /**
   * Return the element data to create or update a form
   *
   * Note: It does not have the ‘sectionId’ field because when the element
   * is sent to create or update a form, the elements inside a section
   * are nested, so it's not needed
   */
  toBackend(): FormsElementInput {
    return {
      id: this.id || undefined,
      label: this.label,
      description: this.description,
      type: this.getType(),
      position: this.position,
      mandatory: this.mandatory,
      params: JSON.stringify(this.params),
      options: this.options.length ? this.options : undefined,
    }
  }

  configureParams(): elementParam[] {
    return []
  }

  static booleanParam(name: string, label: string): booleanParam {
    return {
      type: 'bool',
      name: name,
      label: label,
    }
  }

  static numberParam(name: string, label: string): numberParam {
    return {
      type: 'number',
      name: name,
      label: label,
    }
  }

  getOption(value: string): ElementOption | undefined {
    return this.options.find((op) => op.value === value)
  }
  addOption(option: ElementOption): void {
    this.options.push(option)
  }

  removeOption(value: string): void {
    this.options = this.options.filter((option) => option.value !== value)
  }

  updateOption(value: string, label: string): void {
    this.options = this.options.map((option) =>
      option.value === value ? { ...option, label: label } : option
    )
  }

  updateLabel(newLabel: string): void {
    this.label = newLabel
  }

  updateDescription(newDescription: string): void {
    this.description = newDescription
  }

  updateOptions(newOptions: ElementOption[]): void {
    this.options = newOptions
  }

  lock(): this {
    this.setParam('locked', true)
    return this
  }

  isLocked(): boolean {
    return this.getParam('locked') ?? false
  }
}
